import { ArrowLeft, CheckCircle, Clock, Send, XCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { sendEmail, getEmailStats, getEmailLogs, IEmailLog, EmailStats } from "../../../interfaces/emails";
import { getAllUsers, IUser } from "../../../interfaces/users";
import BONavigation from "../../../components/admin/BONavigation";

export default function AdminEmailSender() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [stats, setStats] = useState<EmailStats | null>(null);
    const [recentEmails, setRecentEmails] = useState<IEmailLog[]>([]);
    const [users, setUsers] = useState<IUser[]>([]);

    const subjectOptions = [
        "Nouvelle opportunité détectée sur votre zone",
        "Nouvelle opportunité détectée",
        "Cession potentielle détectée - âge du fondateur",
        "Problème de CGV détecté",
        "Nouvelle activité d'association détectée",
        "Opportunité d'externalisation juridique détectée",
        "Premier contact ajouté, voici comment maximiser vos chances 💡",
        "Erreur lors de la vérification 3DS",
    ];

    const templateOptions = [
        { value: "alert_sales", label: "alert_sales", defaultSubject: "Nouvelle opportunité détectée sur votre zone" },
        { value: "nouvelle opportunité détecté", label: "nouvelle opportunité détecté", defaultSubject: "Nouvelle opportunité détectée" },
        { value: "first_prospect_added", label: "first_prospect_added", defaultSubject: "Premier contact ajouté, voici comment maximiser vos chances 💡" },
        { value: "3ds-error", label: "3ds-error", defaultSubject: "Erreur lors de la vérification 3DS" },
    ];

    const [formData, setFormData] = useState({
        to: "",
        toName: "Contact",
        subject: "Nouvelle opportunité détectée sur votre zone",
        template: "alert_sales",
        message: "",
    });

    useEffect(() => {
        fetchStats();
        fetchRecentEmails();
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await getAllUsers();
            setUsers(response.data || []);
        } catch (err) {
            console.error("Failed to fetch users:", err);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await getEmailStats(30);
            setStats(response.data);
        } catch (err) {
            console.error("Failed to fetch email stats:", err);
        }
    };

    const fetchRecentEmails = async () => {
        try {
            const response = await getEmailLogs({ page: 1, limit: 10 });
            setRecentEmails(response.data);
        } catch (err) {
            console.error("Failed to fetch recent emails:", err);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        try {
            setLoading(true);

            // Prepare the payload for template-based email
            const variables: Record<string, any> = {};
            if (formData.template === "alert_sales" && formData.message.trim()) {
                variables.message = formData.message.trim();
            }

            const payload: any = {
                to: formData.to.trim(),
                toName: "Contact",
                subject: formData.subject.trim(),
                template: formData.template,
                variables,
            };

            const result = await sendEmail(payload);
            setSuccess(result.message || "Email envoyé avec succès!");

            // Reset form
            setFormData({
                to: "",
                toName: "Contact",
                subject: "Nouvelle opportunité détectée sur votre zone",
                template: "alert_sales",
                message: "",
            });

            // Refresh stats and recent emails
            fetchStats();
            fetchRecentEmails();
        } catch (err: any) {
            console.error("Failed to send email:", err);
            setError(err.response?.data?.message || err.message || "Échec de l'envoi de l'email");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field: string, value: string) => {
        if (field === "template") {
            const tpl = templateOptions.find(t => t.value === value);
            setFormData(prev => ({
                ...prev,
                template: value,
                subject: tpl?.defaultSubject || prev.subject,
                message: value !== "alert_sales" ? "" : prev.message,
            }));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'sent':
                return (
                    <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Envoyé
                    </span>
                );
            case 'failed':
                return (
                    <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
                        <XCircle className="h-3 w-3 mr-1" />
                        Échec
                    </span>
                );
            case 'pending':
                return (
                    <span className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20">
                        <Clock className="h-3 w-3 mr-1" />
                        En attente
                    </span>
                );
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <BONavigation />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate("/admin")}
                        className="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-4"
                    >
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Retour au back-office
                    </button>
                    <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
                        Envoi d'emails
                    </h1>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        Envoyez des emails via Mailgun à vos utilisateurs
                    </p>
                </div>

                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-4 mb-8">
                        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                            <div className="p-5">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <Send className="h-6 w-6 text-gray-400" />
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                                Total (30j)
                                            </dt>
                                            <dd className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {stats.total}
                                            </dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                            <div className="p-5">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <CheckCircle className="h-6 w-6 text-green-400" />
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                                Envoyés
                                            </dt>
                                            <dd className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {stats.sent}
                                            </dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                            <div className="p-5">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <XCircle className="h-6 w-6 text-red-400" />
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                                Échecs
                                            </dt>
                                            <dd className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {stats.failed}
                                            </dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                            <div className="p-5">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <Clock className="h-6 w-6 text-yellow-400" />
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                                En attente
                                            </dt>
                                            <dd className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {stats.pending}
                                            </dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Send Email Form */}
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                                Nouvel email
                            </h2>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            {error && (
                                <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <XCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
                                        </div>
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                                                {error}
                                            </h3>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {success && (
                                <div className="rounded-md bg-green-50 dark:bg-green-900/20 p-4">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <CheckCircle className="h-5 w-5 text-green-400" aria-hidden="true" />
                                        </div>
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                                                {success}
                                            </h3>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div>
                                <label htmlFor="to" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Email destinataire *
                                </label>
                                <input
                                    type="email"
                                    id="to"
                                    required
                                    list="users-datalist"
                                    value={formData.to}
                                    onChange={(e) => handleInputChange("to", e.target.value)}
                                    placeholder="email@exemple.com"
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                                />
                                <datalist id="users-datalist">
                                    {users.map((user) => (
                                        <option key={user._id} value={user.email}>
                                            {user.givenName} {user.familyName}
                                        </option>
                                    ))}
                                </datalist>
                            </div>

                            <div>
                                <label htmlFor="toName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Nom destinataire
                                </label>
                                <input
                                    type="text"
                                    id="toName"
                                    value={formData.toName}
                                    onChange={(e) => handleInputChange("toName", e.target.value)}
                                    disabled
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm opacity-60 cursor-not-allowed"
                                    placeholder="Contact"
                                />
                            </div>

                            <div>
                                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Sujet *
                                </label>
                                <select
                                    id="subject"
                                    required
                                    value={formData.subject}
                                    onChange={(e) => handleInputChange("subject", e.target.value)}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                                >
                                    {subjectOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label htmlFor="template" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Template *
                                </label>
                                <select
                                    id="template"
                                    required
                                    value={formData.template}
                                    onChange={(e) => handleInputChange("template", e.target.value)}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                                >
                                    {templateOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {formData.template === "alert_sales" && (
                                <div>
                                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Message variable
                                    </label>
                                    <textarea
                                        id="message"
                                        rows={3}
                                        value={formData.message}
                                        onChange={(e) => handleInputChange("message", e.target.value)}
                                        placeholder="Contenu du message à insérer dans le template..."
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                                    />
                                </div>
                            )}

                            <div className="flex justify-end">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Send className="h-4 w-4 mr-2" />
                                    {loading ? "Envoi..." : "Envoyer l'email"}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Recent Emails */}
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                                Emails récents
                            </h2>
                        </div>

                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                            {recentEmails.length === 0 ? (
                                <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                                    Aucun email envoyé
                                </div>
                            ) : (
                                recentEmails.map((email) => (
                                    <div key={email._id} className="p-6 hover:bg-[#F9F6F3] dark:hover:bg-gray-700/50">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                    {email.toName || email.to}
                                                </p>
                                                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                                    {email.to}
                                                </p>
                                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mt-1 truncate">
                                                    {email.subject}
                                                </p>
                                                {email.template && (
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                        Template: {email.template}
                                                    </p>
                                                )}
                                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                                    {new Date(email.createdAt).toLocaleString("fr-FR")}
                                                </p>
                                            </div>
                                            <div className="ml-4 flex-shrink-0">
                                                {getStatusBadge(email.status)}
                                            </div>
                                        </div>
                                        {email.error && (
                                            <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                                                Erreur: {email.error}
                                            </p>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
